// components/reports/CourseReport.tsx
import React, { useState } from "react";
import {
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Spin,
  Alert,
  Space,
  Typography,
  Tag,
  Flex,
  Button,
  message,
} from "antd";
import {
  ShoppingOutlined,
  DollarOutlined,
  BarChartOutlined,
  TrophyOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { useQuery } from "react-query";
import {
  customerReportService,
  type ReportParams,
} from "../../services/customerReportService";
import { exportCoursesPopulationReportToExcel } from "../../utils/excelExport";

const { Text } = Typography;

interface CourseReportProps {
  params: ReportParams;
}

const COLORS = ["#667eea", "#52c41a", "#faad14", "#f5222d", "#13c2c2"];

const CourseReport: React.FC<CourseReportProps> = ({ params }) => {
  const [exportLoading, setExportLoading] = useState(false);

  const { data, isLoading, error } = useQuery(
    ["course-report", params],
    () => customerReportService.getCourseReport(params),
    {
      enabled: !!params.startDate && !!params.endDate,
      staleTime: 5 * 60 * 1000,
    }
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="ເກີດຂໍ້ຜິດພາດ"
        description="ບໍ່ສາມາດດຶງຂໍ້ມູນລາຍງານຄອສໄດ້"
        type="error"
        showIcon
      />
    );
  }

  const reportData = data?.data;

  if (!reportData) {
    return (
      <Alert
        message="ບໍ່ມີຂໍ້ມູນ"
        description="ບໍ່ພົບຂໍ້ມູນລາຍງານໃນຊ່ວງເວລາທີ່ເລືອກ"
        type="info"
        showIcon
      />
    );
  }

  // เตรียมข้อมูลสำหรับกราฟ
  const revenueByTypeData =
    reportData &&
    reportData?.revenueByType?.map((item) => ({
      name: item.name,
      revenue: item.totalRevenueLAK,
      revenueTHB: item.totalRevenueTHB,
      count: item.count,
    }));

  const dailyChartData =
    reportData &&
    reportData?.dailyStats?.map((item) => ({
      date: item._id,
      coursesSold: item.coursesSold,
      revenueLAK: item.revenueLAK,
      revenueTHB: item.revenueTHB,
    }));

  // Columns สำหรับ Popular Courses
  const popularCoursesColumns = [
    {
      title: "ລຳດັບ",
      key: "rank",
      width: 80,
      render: (_: any, __: any, index: number) => (
        <Tag
          color={
            index === 0
              ? "gold"
              : index === 1
              ? "silver"
              : index === 2
              ? "#cd7f32"
              : "default"
          }
        >
          #{index + 1}
        </Tag>
      ),
    },
    {
      title: "ຊື່ຄອສ",
      dataIndex: "courseName",
      key: "courseName",
    },

    {
      title: "ຈຳນວນຂາຍ",
      dataIndex: "totalQuantity",
      key: "totalQuantity",
      render: (value: number) => <Text>{value} ຄັ້ງ</Text>,
    },
    {
      title: "ຈຳນວນ Sessions",
      dataIndex: "totalSessions",
      key: "totalSessions",
      render: (value: number) => <Text>{value} ຄັ້ງ</Text>,
    },
    {
      title: "ລາຍຮັບກີບ",
      dataIndex: "totalRevenueLAK",
      key: "totalRevenueLAK",
      render: (value: number) => (
        <Text strong style={{ color: "#52c41a" }}>
          {new Intl.NumberFormat("lo-LA").format(value)} ₭
        </Text>
      ),
      sorter: (a: any, b: any) => a.totalRevenueLAK - b.totalRevenueLAK,
    },
    {
      title: "ລາຍຮັບບາດ",
      dataIndex: "totalRevenueTHB",
      key: "totalRevenueTHB",
      render: (value: number) => (
        <Text strong style={{ color: "#52c41a" }}>
          {new Intl.NumberFormat("lo-LA").format(value)} <del>B</del>
        </Text>
      ),
      sorter: (a: any, b: any) => a.totalRevenueTHB - b.totalRevenueTHB,
    },
  ];

  // ฟังก์ชัน Export
  const handleExportToExcel = async () => {
    setExportLoading(true);
    try {
      const dateRangeInfo = {
        startDate: params.startDate,
        endDate: params.endDate,
      };

      // ✅ แก้ไขตรงนี้
      if (reportData.popularCourses) {
        exportCoursesPopulationReportToExcel(
          reportData.popularCourses,
          dateRangeInfo
        );
        message.success("ສົ່ງອອກລາຍງານຄອສສຳເລັດ");
      } else {
        message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກ");
    } finally {
      setExportLoading(false);
    }
  };
  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ລາຍຮັບລວມກີບ
                </Text>
              }
              value={reportData?.summary?.totalRevenueLAK}
              suffix="₭"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ລາຍຮັບລວມບາດ
                </Text>
              }
              value={reportData?.summary?.totalRevenueTHB}
              suffix="B"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ຄອສທີ່ຂາຍໄດ້
                </Text>
              }
              value={reportData?.summary?.totalCoursesSold}
              suffix="ຄັ້ງ"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<ShoppingOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  Sessions ທັງໝົດ
                </Text>
              }
              value={reportData?.summary?.totalSessions}
              suffix="ຄັ້ງ"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]}>
        {/* Revenue by Type */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BarChartOutlined style={{ color: "#667eea" }} />
                <Text strong>ລາຍຮັບຕາມປະເພດຄອສ</Text>
              </Space>
            }
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={revenueByTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent = 0 }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="revenue"
                >
                  {revenueByTypeData?.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) =>
                    new Intl.NumberFormat("lo-LA").format(value as number) +
                    " ₭"
                  }
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Daily Sales */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BarChartOutlined style={{ color: "#667eea" }} />
                <Text strong>ການຂາຍຄອສແຕ່ລະວັນ</Text>
              </Space>
            }
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#8c8c8c" />
                <YAxis stroke="#8c8c8c" />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="coursesSold"
                  stroke="#667eea"
                  strokeWidth={2}
                  name="ຈຳນວນຂາຍ"
                />
                <Line
                  type="monotone"
                  dataKey="revenueLAK"
                  stroke="#007c25ff"
                  strokeWidth={2}
                  name="ເງິນກີບ"
                />
                <Line
                  type="monotone"
                  dataKey="revenueTHB"
                  stroke="#004f07ff"
                  strokeWidth={2}
                  name="ເງິນບາດ"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Popular Courses Table */}
      <Card
        title={
          <Flex justify="space-between" align="center">
            <Space>
              <TrophyOutlined style={{ color: "#667eea" }} />
              <Text strong>ຄອສຍອດນິຍົມ</Text>
            </Space>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportToExcel}
              loading={exportLoading}
              style={{
                background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                border: "none",
              }}
            >
              ສົ່ງອອກ Excel
            </Button>
          </Flex>
        }
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
      >
        <Table
          dataSource={reportData.popularCourses}
          columns={popularCoursesColumns}
          pagination={false}
          rowKey="_id"
          loading={exportLoading}
        />
      </Card>
    </Space>
  );
};

export default CourseReport;
